import { ProjectStatusType } from '@/types';

export interface StatusOption {
  value: ProjectStatusType;
  label: string;
}

export const PROJECT_STATUSES: StatusOption[] = [
  { value: 'planning', label: 'Planning' },
  { value: 'in progress', label: 'In Progress' },
  { value: 'on hold', label: 'On Hold' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'completed', label: 'Completed' },
];

/**
 * Maps an EBProjects numeric StatusId to a canonical PROJECT_STATUSES value.
 * Mirrors EBProjects ProjectsController.ResolveStatusName:
 * 1=Planning, 2=In Progress, 3=On Hold, 4=Reopened, 5=Cancelled, 6=Completed, 7=Opportunity.
 */
const normalizeStatusId = (statusId: number): ProjectStatusType | undefined => {
  switch (statusId) {
    case 1: return 'planning';
    case 2: return 'in progress';
    case 3: return 'on hold';
    case 4: return 'in progress'; // Reopened
    case 5: return 'cancelled';
    case 6: return 'completed';
    case 7: return 'planning'; // Opportunity
    default: return undefined;
  }
};

/**
 * Normalizes a raw status string to one of the canonical PROJECT_STATUSES values.
 * Mirrors the backend NormalizeCostCentreStatus / NormalizeProjectStatus logic so
 * that settings values like 'active' or 'reopened' correctly map to the UI statuses.
 * When the string is null/empty/unrecognized, falls back to the numeric StatusId
 * so project statuses stay consistent with EBProjects.
 */
export const normalizeStatusValue = (
  status: string | null | undefined,
  statusId?: number | null,
): ProjectStatusType => {
  const s = (status ?? '').trim().toLowerCase();
  switch (s) {
    case 'planning':
    case 'planned':
    case 'estimate':
    case 'opportunity':
      return 'planning';
    case 'in progress':
    case 'in-progress':
    case 'inprogress':
    case 'active':
    case 'reopened':
      return 'in progress';
    case 'on hold':
    case 'on-hold':
    case 'onhold':
    case 'suspended':
      return 'on hold';
    case 'cancelled':
    case 'canceled':
    case 'archived':
      return 'cancelled';
    case 'completed':
    case 'complete':
    case 'finished':
    case 'closed':
      return 'completed';
    default:
      return (statusId != null ? normalizeStatusId(statusId) : undefined) ?? 'planning';
  }
};
